import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLocation } from "wouter";
import { ArrowLeft, UserPlus, Calendar, Phone, Mail, Building, User, Shield, Upload, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useState, useRef } from "react";

// Comprehensive employee schema
const addEmployeeSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  employeeId: z.string().min(1, "Employee ID is required"),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  phoneNumber: z.string().min(10, "Please enter a valid phone number").optional().or(z.literal("")),
  gender: z.enum(['male', 'female', 'other', 'prefer_not_to_say'], {
    required_error: "Please select a gender",
  }),
  dateOfJoining: z.string().min(1, "Date of joining is required"),
  department: z.string().min(1, "Department is required"),
  designation: z.string().min(1, "Designation is required"),
  manager: z.string().min(1, "Manager is required"),
  workLocation: z.string().min(1, "Work location is required"),
  linkedInUrl: z.string().url("Please enter a valid LinkedIn URL").optional().or(z.literal("")),
  profilePictureUrl: z.string().optional(),
  buddy: z.string().optional(),
});

type AddEmployeeForm = z.infer<typeof addEmployeeSchema>;

export default function AddEmployee() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch users for manager/buddy dropdowns
  const { data: dropdownUsers } = useQuery({
    queryKey: ['/admin/users/dropdown'],
    queryFn: async () => {
      const response = await apiRequest("GET", "/admin/users/dropdown");
      return response.json();
    },
  });

  // Fetch master data for dropdowns
  const { data: departments } = useQuery({
    queryKey: ['/admin/departments'],
    queryFn: async () => {
      const response = await apiRequest("GET", "/admin/departments");
      return response.json();
    },
  });

  const { data: designations } = useQuery({
    queryKey: ['/admin/designations'],
    queryFn: async () => {
      const response = await apiRequest("GET", "/admin/designations");
      return response.json();
    },
  });

  const { data: officeLocations } = useQuery({
    queryKey: ['/admin/office-locations'],
    queryFn: async () => {
      const response = await apiRequest("GET", "/admin/office-locations");
      return response.json();
    },
  });

  const form = useForm<AddEmployeeForm>({
    resolver: zodResolver(addEmployeeSchema),
    defaultValues: {
      email: "",
      employeeId: "",
      firstName: "",
      lastName: "",
      phoneNumber: "",
      gender: "prefer_not_to_say" as const,
      dateOfJoining: "",
      department: "",
      designation: "",
      manager: "",
      workLocation: undefined,
      linkedInUrl: "",
      profilePictureUrl: "",
      buddy: "",
    },
  });

  // Upload image to Firebase
  const uploadImageToFirebase = async (file: File): Promise<string> => {
    try {
      setIsUploading(true);
      const { storage, ensureAuthenticated } = await import('@/lib/firebase');
      const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
      
      await ensureAuthenticated();
      
      const timestamp = Date.now();
      const fileName = `user-photographs/${timestamp}-${file.name}`;
      const storageRef = ref(storage, fileName);
      
      const snapshot = await uploadBytes(storageRef, file, {
        contentType: file.type
      });
      
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error) {
      console.error("Firebase upload error:", error);
      throw new Error("Failed to upload image. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const addEmployeeMutation = useMutation({
    mutationFn: async (data: AddEmployeeForm) => {
      let profilePictureUrl = data.profilePictureUrl;
      
      // Upload image if selected
      if (selectedImage) {
        profilePictureUrl = await uploadImageToFirebase(selectedImage);
      }
      
      const employeeData = {
        ...data,
        username: data.email, // Set username from email
        companyEmail: data.email, // Set companyEmail for employees
        password: null, 
        role: "employee" as const, // Always set role to employee
        departmentId: parseInt(data.department, 10), // Convert string ID to number
        designationId: parseInt(data.designation, 10), // Convert string ID to number
        officeLocationId: parseInt(data.workLocation, 10), // Convert string ID to number
        managerUserId: data.manager ? parseInt(data.manager, 10) : null, // Convert manager ID to number
        buddyUserId: data.buddy ? parseInt(data.buddy, 10) : null, // Convert buddy ID to number
        emergencyContactName: "TBD", // Default emergency contact
        emergencyContactPhone: "TBD", // Default emergency contact phone
        profilePictureUrl: profilePictureUrl || null,
      } as any; // Type assertion to handle the additional fields
      // Remove the string fields that were replaced with IDs
      delete employeeData.department;
      delete employeeData.designation;
      delete employeeData.workLocation;
      const response = await apiRequest("POST", "/admin/users", employeeData);
      return response.json();
    },
    onSuccess: (data) => {
      // Check if calendar events were created
      if (data.user?.calendarEvents) {
        toast({
          title: "Success! 🎉",
          description: "Employee added successfully! Google Calendar meetings have been scheduled and invites sent.",
        });
        
        // Show calendar events info
        setTimeout(() => {
          toast({
            title: "📅 Calendar Events Created",
            description: `Onboarding meetings scheduled for ${data.user.firstName} ${data.user.lastName}.`,
          });
        }, 1000);
      } else {
        toast({
          title: "Success",
          description: "Employee added successfully!",
        });
      }
      
      queryClient.invalidateQueries({ queryKey: ['/admin/users'] });
      // Redirect to the root path which will show the appropriate dashboard based on user role
      setLocation('/');
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add employee",
        variant: "destructive",
      });
    },
  });

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Invalid File Type",
          description: "Please select an image file",
          variant: "destructive",
        });
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: "Image must be less than 5MB",
          variant: "destructive",
        });
        return;
      }
      
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    form.setValue('profilePictureUrl', '');
  };

  const onSubmit = (data: AddEmployeeForm) => {
    addEmployeeMutation.mutate(data);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Back Button */}
        <div className="mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLocation('/')}
            className="text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors duration-200"
            data-testid="button-back-to-admin"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
        </div>

        {/* Form Card */}
        <Card className="shadow-xl border border-gray-200 bg-white">
          <CardHeader className="pb-6 border-b border-gray-100">
            <CardTitle className="text-2xl font-bold text-gray-900 mb-2">Add New Employee</CardTitle>
            <p className="text-gray-600 text-sm">Fill in the employee's details to create their account.</p>
          </CardHeader>
          <CardContent className="p-6">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                {/* Personal Details */}
                <div className="space-y-5 overflow-visible isolation-auto">
                  <div className="flex items-center space-x-3 pb-3 border-b-2 border-gray-200">
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800">Personal Details</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-gray-700 mb-2 block">First Name</FormLabel>
                          <FormControl>
                            <Input {...field} data-testid="input-first-name" className="h-11 border-2 border-gray-200 focus:border-gray-400 focus:ring-2 focus:ring-gray-100 transition-all duration-200 rounded-lg" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-gray-700 mb-2 block">Last Name</FormLabel>
                          <FormControl>
                            <Input {...field} data-testid="input-last-name" className="h-11 border-2 border-gray-200 focus:border-gray-400 focus:ring-2 focus:ring-gray-100 transition-all duration-200 rounded-lg" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-gray-700 mb-2 block">Email</FormLabel>
                          <FormControl>
                            <Input {...field} type="email" data-testid="input-email" className="h-11 border-2 border-gray-200 focus:border-gray-400 focus:ring-2 focus:ring-gray-100 transition-all duration-200 rounded-lg" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="employeeId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-gray-700 mb-2 block">Employee ID</FormLabel>
                          <FormControl>
                            <Input {...field} data-testid="input-employee-id" className="h-11 border-2 border-gray-200 focus:border-gray-400 focus:ring-2 focus:ring-gray-100 transition-all duration-200 rounded-lg" placeholder="e.g., EMP001" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <FormField
                      control={form.control}
                      name="phoneNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-gray-700 mb-2 block">Phone Number (Optional)</FormLabel>
                          <FormControl>
                            <Input {...field} data-testid="input-phone" className="h-11 border-2 border-gray-200 focus:border-gray-400 focus:ring-2 focus:ring-gray-100 transition-all duration-200 rounded-lg" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <FormField
                      control={form.control}
                      name="gender"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-slate-700 mb-2 block">Gender *</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger 
                                data-testid="select-gender"
                                className="w-full h-11 border-2 border-slate-200 rounded-lg bg-white hover:border-purple-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-200"
                              >
                                <SelectValue placeholder="Select gender (required)" className="text-slate-500" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent 
                              className="z-[99999] bg-white border-2 border-purple-200 rounded-lg shadow-xl"
                              side="bottom" 
                              align="start" 
                              sideOffset={4}
                              avoidCollisions={true}
                              collisionPadding={20}
                            >
                              <SelectItem value="male" className="hover:bg-purple-50 cursor-pointer text-slate-700">Male</SelectItem>
                              <SelectItem value="female" className="hover:bg-purple-50 cursor-pointer text-slate-700">Female</SelectItem>
                              <SelectItem value="other" className="hover:bg-purple-50 cursor-pointer text-slate-700">Other</SelectItem>
                              <SelectItem value="prefer_not_to_say" className="hover:bg-purple-50 cursor-pointer text-slate-700">Prefer not to say</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="linkedInUrl"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-gray-700 mb-2 block">LinkedIn URL (Optional)</FormLabel>
                          <FormControl>
                            <Input {...field} type="url" placeholder="https://linkedin.com/in/username" data-testid="input-linkedin" className="h-11 border-2 border-gray-200 focus:border-gray-400 focus:ring-2 focus:ring-gray-100 transition-all duration-200 rounded-lg placeholder:text-gray-400" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-5">
                    <FormField
                      control={form.control}
                      name="profilePictureUrl"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-gray-700 mb-2 block">Profile Picture (Optional)</FormLabel>
                          <FormControl>
                            <div className="space-y-4">
                              {imagePreview ? (
                                <div className="relative inline-block">
                                  <img
                                    src={imagePreview}
                                    alt="Profile preview"
                                    className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200"
                                  />
                                  <button
                                    type="button"
                                    onClick={removeImage}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                              ) : (
                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                                  <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                  <p className="text-sm text-gray-600 mb-2">Upload profile picture</p>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={isUploading}
                                    className="text-sm"
                                  >
                                    {isUploading ? "Uploading..." : "Choose File"}
                                  </Button>
                                  <p className="text-xs text-gray-500 mt-2">Max 5MB, JPG/PNG</p>
                                </div>
                              )}
                              <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleImageSelect}
                                className="hidden"
                                data-testid="input-profile-picture"
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Employment Details */}
                <div className="space-y-5">
                  <div className="flex items-center space-x-3 pb-3 border-b-2 border-purple-100">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">Employment Details</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <FormField
                      control={form.control}
                      name="dateOfJoining"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-slate-700 mb-2 block">Date of Joining</FormLabel>
                          <FormControl>
                            <Input {...field} type="date" data-testid="input-joining-date" className="h-11 border-2 border-slate-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-200 rounded-lg" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                    control={form.control}
                    name="department"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-slate-700 mb-2 block">Department</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger 
                              data-testid="select-add-department"
                              className="w-full h-11 border-2 border-slate-200 rounded-lg bg-white hover:border-purple-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-200"
                            >
                              <SelectValue placeholder="Select department" className="text-slate-500" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="z-[99999] bg-white border-2 border-purple-200 rounded-lg shadow-xl">
                            {departments?.map((dept: any) => (
                              <SelectItem 
                                key={dept.id} 
                                value={dept.id.toString()} 
                                className="hover:bg-purple-50 cursor-pointer text-slate-700"
                              >
                                {dept.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  </div>

               
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormField
                    control={form.control}
                    name="designation"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-slate-700 mb-2 block">Designation</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger 
                              data-testid="select-add-designation"
                              className="w-full h-11 border-2 border-slate-200 rounded-lg bg-white hover:border-purple-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-200"
                            >
                              <SelectValue placeholder="Select designation" className="text-slate-500" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="z-[99999] bg-white border-2 border-purple-200 rounded-lg shadow-xl" position="popper" side="bottom" align="start" sideOffset={4}>
                            {designations?.map((designation: any) => (
                              <SelectItem 
                                key={designation.id} 
                                value={designation.id.toString()} 
                                className="hover:bg-purple-50 cursor-pointer text-slate-700"
                              >
                                {designation.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="manager"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-slate-700 mb-2 block">Manager *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger 
                              data-testid="select-manager"
                              className="w-full h-11 border-2 border-slate-200 rounded-lg bg-white hover:border-purple-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-200"
                            >
                              <SelectValue placeholder="Select manager (required)" className="text-slate-500" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="z-[99999] bg-white border-2 border-purple-200 rounded-lg shadow-xl">
                            {dropdownUsers?.map((user: any) => (
                              <SelectItem 
                                key={user.id} 
                                value={user.id.toString()} 
                                className="hover:bg-purple-50 cursor-pointer text-slate-700"
                              >
                                {`${user.firstName} ${user.lastName}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <FormField
                    control={form.control}
                    name="workLocation"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-slate-700 mb-2 block">Work Location</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-work-location" className="w-full h-11 border-2 border-slate-200 rounded-lg bg-white hover:border-purple-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-200">
                              <SelectValue placeholder="Select work location" className="text-slate-500" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="z-[99999] bg-white border-2 border-purple-200 rounded-lg shadow-xl">
                            {officeLocations?.map((location: any) => (
                              <SelectItem 
                                key={location.id} 
                                value={location.id.toString()} 
                                className="hover:bg-purple-50 cursor-pointer text-slate-700"
                              >
                                {location.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <FormField
                    control={form.control}
                    name="buddy"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-slate-700 mb-2 block">Buddy (Optional)</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger 
                              data-testid="select-buddy"
                              className="w-full h-11 border-2 border-slate-200 rounded-lg bg-white hover:border-purple-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-200"
                            >
                              <SelectValue placeholder="Select buddy" className="text-slate-500" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="z-[99999] bg-white border-2 border-purple-200 rounded-lg shadow-xl">
                            {dropdownUsers?.map((user: any) => (
                              <SelectItem 
                                key={user.id} 
                                value={user.id.toString()} 
                                className="hover:bg-purple-50 cursor-pointer text-slate-700"
                              >
                                {`${user.firstName} ${user.lastName}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>


              {/* Submit Button */}
              <div className="flex justify-end gap-4 pt-8 border-t-2 border-slate-100">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setLocation('/')}
                  data-testid="button-cancel"
                  className="border-2 border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 px-6 py-2"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={addEmployeeMutation.isPending}
                  data-testid="button-submit-employee"
                  className="bg-servify-purple hover:bg-servify-purple-dark text-white"
                >
                  {addEmployeeMutation.isPending ? "Adding..." : "Add Employee"}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
        </div>
    </div>
  );
} 